import BaseApi from "./base-api";

/**
 * Class for the Sungage API
 *
 * @extends BaseApi
 * @description This class is used to interact with the Sungage API
 *
 * @note There are addional sungage methods in the proposal-tool-api.js file
 */
export class SungageApi extends BaseApi {
  constructor() {
 super("sungage");   

    this.testId = Math.random().toString(36).substring(7);

    this.methods = {
      GET_DISCLOSURES: "get_disclosures",
      GET_PRE_DISCLOSURES: "get_pre_disclosures",
      GET_RATES: "get_rates",
      UPDATE_APPLICATION: "update_application",
      SUBMIT_APPLICATION: "submit_application",
      GET_PROJECT: "get_project",
      CREATE_PROJECT: "create_project",
      UPDATE_PROJECT: "update_project",
      ADD_SUNGAGE_DATA: "add_sungage_data", // this is used on the proposal-tool endpoint
      GET_SUNGAGE_DATA: "get_sungage_data", // this is used on the proposal-tool endpoint
      GET_UTILITIES: "get_utilities",
      CALCULATE_MONTHLY_PAYMENTS: "calculate_monthly_payments",
    };
  }

  /**
   * Get the Sungage ID from the sungage table
   *
   * @note This endpoint is part of the proposal_tool endpoint
   */
  getData = async ({ projectId }) => {
    const response = await this.request({
      method: this.methods.GET_SUNGAGE_DATA,
      sf_project_id: projectId,
      options: {
        override: {
          resource: "proposal-tool",
        },
      },
    });

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response?.message || "Could not get Sungage Data");
  };

  /**
   * Add data to the sungage table
   *
   * @note This is only used to add the sungage ID to the project
   * @note This endpoint is part of the proposal_tool endpoint
   */
  addData = async ({ projectId, sungageId }) => {
    const response = await this.request({
      method: this.methods.ADD_SUNGAGE_DATA,
      sf_project_id: projectId,
      sungage_id: sungageId,
      options: {
        override: {
          resource: "proposal-tool",
        },
      },
    });

    if (response.success) {
      return response.data;
    }

    throw new Error(response?.message || "Could not add Sunage Data");
  };

  /**
   * Get url for the credit application
   *
   * @note You can also pass in "start" or "email" for the action parameter. "start" does not work though.
   */
  submitApplication = async ({
    projectId,
    sungageId,
    action = "url",
    username,
  }) => {
    const response = await this.request({
      method: this.methods.SUBMIT_APPLICATION,
      id: projectId,
      username,
      sungageId,
      action,
    });

    if (response.success || response.data) {
      return response.data;
    }

    throw new Error(response?.message || "Could not submit application");
  };

  /**
   * Create a project in Sungage
   *
   * @note this will return a success: false, even though it does create a project
   */
  createProject = async ({ projectId, id, ...data }) => {
    const response = await this.request({
      method: this.methods.CREATE_PROJECT,
      id: id,
      ...data,
    });

    if (response.success || response.data) {
      return response.data;
    }

    throw new Error(response?.message || "Could not create project");
  };

  /**
   * Get a project from Sungage by project ID and Sungage ID
   */
  getProject = async ({ projectId, sungageId, username }) => {
    const response = await this.request({
      method: this.methods.GET_PROJECT,
      username,
      id: projectId,
      sungageId,
    });

    if (response.success) {
      return response.data;
    }

    throw new Error(response?.message || "Could not get Sungage project");
  };

  /**
   * Calculate Monthly Payments from Sungage
   *
   * @param {Object} data
   * @param {String} data.state
   * @param {Number} data.term
   * @param {Number} data.rate
   * @param {Number} data.solarAmount
   * @param {Number} data.solarSize
   * @param {Number} data.batteryAmount
   * @param {Number} data.roofAmount
   * @param {Number} data.downPayment
   * @param {Number} data.solarRebate
   * @param {Boolean} data.solarRebatePaidToHomeowner
   * @param {Number} data.batteryRebate
   * @param {Boolean} data.batteryRebatePaidToHomeowner
   * @param {Boolean} data.ach
   * @param {Boolean} data.paydown
   * @param {String} data.dppType
   * @param {Number} data.itc
   * @param {String} data.utility
   */
  calculateMonthlyPayments = async (data) => {
    const response = await this.request({
      method: this.methods.CALCULATE_MONTHLY_PAYMENTS,
      ...data,
    });

    if (response.success) {
      return response.data;
    }

    throw new Error(
      response?.message || "Could not calculate monthly payments"
    );
  };
}

export default new SungageApi();
